import { cloneDeep, pick, merge, map } from 'lodash';
import { EventEmitter } from '../../utils/event-emitter';

const MAX_HISTORY_LENGTH = 100;

/** @type {BadgeEditor.State} */
export const BLANK_STATE = {
    blocks: {
        image: [],
        text: [],
        qrcode: [],
        background: {},
    },
};

export class EditorState extends EventEmitter {
    /**
     * @param {BadgeEditor.StateOrEmpty} initialState
     */
    constructor(initialState) {
        super();

        /** @type {BadgeEditor.State} */
        const state = merge(initialState, BLANK_STATE);

        /** @type {BadgeEditor.State[]} */
        this.history = [state];

        /** @type {number} */
        this._currentIndex = 0;
    }

    /**
     * Returns current state.
     */
    get current() {
        return this.history[this._currentIndex];
    }

    /**
     * Create a new point in history
     *
     * @param {(state: BadgeEditor.State) => void} callback Function that is called with new deep-cloned state
     * @emits EditorState#state
     */
    update(callback) {
        const newState = cloneDeep(this.current);

        callback(newState);

        this.history.splice(0, this._currentIndex, newState);
        this._currentIndex = 0;

        if (this.history.length > MAX_HISTORY_LENGTH) {
            this.history.splice(MAX_HISTORY_LENGTH, this.history.length - MAX_HISTORY_LENGTH);
        }

        this.emit('state', newState);
    }

    replace(value) {
        const newState = cloneDeep(value);

        this.history = [newState];
        this._currentIndex = 0;

        this.emit('state', newState);
    }

    getIds() {
        const blocks = this.current.blocks;
        return ['text', 'image', 'qrcode']
            .reduce((result, key) => result.concat(map(blocks?.[key], '_id') ?? []), []);
    }

    /**
     * Whether state can be undoed
     */
    get canUndo() {
        return this._currentIndex < this.history.length - 1;
    }

    /**
     * Undo to previous state
     *
     * @emits EditorState#state
     */
    undo() {
        this._currentIndex = Math.min(this._currentIndex + 1, this.history.length - 1);

        this.emit('state', this.current);
    }

    /**
     * Whether state can be redoed
     */
    get canRedo() {
        return this._currentIndex > 0;
    }

    /**
     * Redo to next state
     *
     * @emits EditorState#state
     */
    redo() {
        this._currentIndex = Math.max(this._currentIndex - 1, 0);

        this.emit('state', this.current);
    }
}

/**
 * @typedef TextboxProps
 * @property {string} id
 * @property {'Textbox'} type
 * @property {string} text
 * @property {string} fill CSS <canvas> data type
 * @property {number} width
 * @property {number} height
 * @property {number} top Equivalent to the Y coordinate
 * @property {number} left Equivalent to the X coordinate
 * @property {number} minFontSize
 * @property {number} fontSize
 * @property {'left'|'center'|'right'} textAlign
 */

const BASE_PROPS = [
    '_id',
    'id',
];
const POSITION_PROPS = [
    'top',
    'left',
    'width',
    'height',
    'angle',
    'originY',
    'originX',
];

const SCALE_PROPS = [
    'scaleX',
    'scaleY',
];

export const TEXTBOX_PROPS = [
    ...BASE_PROPS,
    ...POSITION_PROPS,

    'fill',
    'text',

    'fontSize',
    'maxFontSize',
    'minFontSize',

    'useDynamicFontSize',

    'textAlign',

    'fontFamily',
    'fontFaceId',
];

export const IMAGE_PROPS = [
    ...BASE_PROPS,
    ...POSITION_PROPS,
    ...SCALE_PROPS,
    'isImage',
];

export const QR_CODE_PROPS = [
    ...BASE_PROPS,
    ...POSITION_PROPS,
    ...SCALE_PROPS,
    'isQrCode',
    'content',
];

/**
 * Sanitizes Textbox props
 * @param {any} json
 * @returns {Partial<TextboxProps>}
 */
export function sanitizeTextboxProps(json) {
    return pick(json, TEXTBOX_PROPS);
}

export function sanitizeImageProps(json) {
    return pick(json, IMAGE_PROPS);
}

export function sanitizeQrCodeProps(json) {
    return pick(json, QR_CODE_PROPS);
}
